import { createReporter, type PackageReporter } from '@ergeon/erg-utils-js/exports/sentry/reporting';
import { withScope } from '@sentry/react';

import { isRecord } from './isRecord';

/**
 * App-scoped reporter. Severity contract: errors are failed/invalid outcomes;
 * warnings are operations completed on a verified correctness-preserving
 * fallback. Safe no-op before Sentry.init.
 *
 * Call sites pass only what varies — `operation` (stable snake_case title, no
 * ids/amounts in it), `handled`, and safe scalar `extra` context. Whole quote,
 * calc_input, auth-user or response objects must never be attached — pass
 * individual ids, counts and status codes instead.
 */
export const { reportError, reportWarning }: PackageReporter = createReporter({
  package: 'cad.ergeon.in',
  origin: 'app',
});

/** HTTP status of an axios-style failure, or undefined for network/non-HTTP errors. */
export const getResponseStatus = (error: unknown): number | undefined => {
  const response = isRecord(error) ? error.response : undefined;
  const status = isRecord(response) ? response.status : undefined;
  return typeof status === 'number' ? status : undefined;
};

/** Coarse status bucket: `4xx`, `5xx`, … or `network` when no response arrived. */
export const getStatusClass = (error: unknown): string => {
  const status = getResponseStatus(error);
  return status === undefined ? 'network' : `${Math.floor(status / 100)}xx`;
};

/** An axios rejection caused by our own AbortController, not by a failure. */
export const isCanceledRequest = (error: unknown): boolean =>
  isRecord(error) && (error.code === 'ERR_CANCELED' || error.name === 'CanceledError');

/**
 * `reportError` for a failed HTTP request, with the status bucket promoted to
 * an indexed `status_class` tag.
 *
 * `extra` is not indexed — not searchable, not usable in alert rules or
 * dashboards — and the shared fingerprint groups by package + operation, so
 * without this a 404 from a stale quote id sits in the same issue as a 502
 * outage with identical tags, and "alert when quote_data_load 5xx exceeds N"
 * is not expressible. The reporter passes its tags through Sentry's
 * `captureContext`, which merges into the scope's tags rather than replacing
 * them, so the scope is the place to add one.
 */
export const reportRequestError = (
  error: unknown,
  metadata: Parameters<PackageReporter['reportError']>[1],
): void => {
  withScope((scope) => {
    scope.setTag('status_class', getStatusClass(error));
    reportError(error, metadata);
  });
};
